export type MutationCallback = (mutations: MutationRecord[], observer: MutationObserver) => void;

export default class LazyMutationCallback {
  private callback: MutationCallback;
  private idleTimeout: number;
  private timeoutId: number | null = null;
  private pendingMutations: MutationRecord[] = [];
  private observer: MutationObserver | null = null;

  constructor(callback: MutationCallback, idleTimeout: number = 500) {
    this.callback = callback;
    this.idleTimeout = idleTimeout;
    this.handleMutations = this.handleMutations.bind(this);
  }

  private handleMutations(mutations: MutationRecord[], observer: MutationObserver): void {
    // Add new mutations to pending list
    this.pendingMutations.push(...mutations);

    // Clear existing timeout if there is one
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
    }

    // Set new timeout
    this.timeoutId = window.setTimeout(() => {
      if (this.pendingMutations.length > 0) {
        // Call the original callback with accumulated mutations
        this.callback(this.pendingMutations, observer);
        // Clear the pending mutations
        this.pendingMutations = [];
      }
      this.timeoutId = null;
    }, this.idleTimeout);
  }

  observe(target: Node, options?: MutationObserverInit): void {
    // Create new observer if it doesn't exist
    if (!this.observer) {
      this.observer = new MutationObserver(this.handleMutations);
    }
    this.observer.observe(target, options);
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.pendingMutations = [];
  }

  takeRecords(): MutationRecord[] {
    if (this.observer) {
      const records = this.observer.takeRecords();
      this.pendingMutations.push(...records);
      return [...this.pendingMutations];
    }
    return [];
  }
}
